import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Loader2, Send } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [already, setAlready] = useState(false);
  const [manageToken, setManageToken] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/verify/${token}`);
        if (r.data?.verified) {
          setStatus("ok");
          setAlready(r.data.already);
          setManageToken(r.data.manage_token);
          try { localStorage.setItem("weathervane:last-token", r.data.manage_token); } catch {}
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendNow = async () => {
    if (!manageToken || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/manage/${manageToken}/send-now`);
      setSentCount(r.data?.sent || 0);
      toast.success(
        r.data?.sent > 1
          ? `Sent ${r.data.sent} forecast emails. Check your inbox!`
          : "Forecast email sent. Check your inbox!"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send right now. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-2xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-black bg-white p-10" data-testid="verify-card">
          {status === "loading" && (
            <div className="flex items-center gap-3"><Loader2 className="w-6 h-6 animate-spin" /> Confirming…</div>
          )}
          {status === "ok" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-[#FFD600] mb-6">
                <Check className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">{already ? "Already confirmed" : "Confirmed"}</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">You&rsquo;re all set.</h1>
              <p className="mt-6 text-neutral-700">Your daily forecast is active. First email will arrive at your chosen delivery time.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={sendNow}
                  disabled={sending || sentCount !== null}
                  data-testid="verify-send-now-button"
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sentCount !== null
                    ? `Sent ${sentCount > 1 ? `${sentCount} emails` : "— check inbox"}`
                    : "Send me one right now"}
                </button>
                {manageToken && (
                  <button
                    onClick={() => navigate(`/manage/${manageToken}`)}
                    data-testid="go-to-manage-button"
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
                  >
                    Manage my subscriptions
                  </button>
                )}
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6"><X className="w-6 h-6" /></div>
              <h1 className="font-display text-3xl font-black tracking-tighter">We couldn&rsquo;t confirm that link.</h1>
              <p className="mt-4 text-neutral-700">It may have already been used or expired. Try subscribing again.</p>
              <Link to="/" className="underline text-sm mt-6 inline-block">Back to sign up →</Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
